import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import './QBOHistory.css';

const QBOHistory = () => {
  const [monthData, setMonthData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadingMonth, setDownloadingMonth] = useState(null);
  const [refreshingMonth, setRefreshingMonth] = useState(null);

  useEffect(() => {
    fetchMonthData();
  }, []);

  const fetchMonthData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiCall('/api/qbo-history/months', {
        method: 'GET'
      });

      if (response.ok) {
        const data = await response.json();
        setMonthData(data.months || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch QBO history data');
      }
    } catch (err) {
      setError('Network error occurred while fetching QBO history data');
    } finally {
      setLoading(false);
    }
  };

  const downloadMonth = async (month) => {
    setDownloadingMonth(month);
    setError('');
    
    try {
      const response = await apiCall('/api/qbo-history/download-month', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ month: month })
      });

      if (response.ok) {
        const data = await response.json();
        // Refresh the month data to show updated status
        await fetchMonthData();
        alert(`Successfully downloaded data for ${month}`);
      } else {
        const errorData = await response.json();
        setError(errorData.error || `Failed to download data for ${month}`);
      }
    } catch (err) {
      setError(`Network error occurred while downloading ${month}`);
    } finally {
      setDownloadingMonth(null);
    }
  };

  const refreshMonth = async (month) => {
    setRefreshingMonth(month);
    setError('');
    
    try {
      const response = await apiCall('/api/qbo-history/refresh-month', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ month: month })
      });

      if (response.ok) {
        const data = await response.json();
        // Refresh the month data to show updated status
        await fetchMonthData();
        alert(`Successfully refreshed data for ${month}`);
      } else {
        const errorData = await response.json();
        setError(errorData.error || `Failed to refresh data for ${month}`);
      }
    } catch (err) {
      setError(`Network error occurred while refreshing ${month}`);
    } finally {
      setRefreshingMonth(null);
    }
  };

  const formatMonth = (monthStr) => {
    // Parse the date string properly (YYYY-MM-DD format)
    const [year, month, day] = monthStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month - 1 because JS months are 0-indexed
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  const getStatusIcon = (hasData) => {
    return hasData ? '✅' : '📄';
  };

  const getStatusText = (hasData) => {
    return hasData ? 'Available' : 'Not Downloaded';
  };

  const getStatusClass = (hasData) => {
    return hasData ? 'status-available' : 'status-not-downloaded';
  };

  const isReadyForDownload = (month) => {
    const monthDate = new Date(month);
    const currentDate = new Date();
    
    // Check if this month is in the past and not the current month
    return monthDate.getFullYear() < currentDate.getFullYear() || 
           (monthDate.getFullYear() === currentDate.getFullYear() && 
            monthDate.getMonth() < currentDate.getMonth());
  };

  return (
    <div className="qbo-history">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>QBO History Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>View QBO account history data by month</li>
              <li>Download missing months from QBO API</li>
              <li>Refresh existing months with updated data</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        <div className="qbo-history-header">
          <h1>QBO History Data</h1>
          <p>Manage QBO account history data by month</p>
        </div>

        <div className="qbo-history-content">
          <div className="controls-section">
            <div className="refresh-button-container">
              <button 
                className="btn btn-secondary refresh-data-btn"
                onClick={fetchMonthData}
                disabled={loading}
              >
                {loading ? 'Loading...' : '🔄 Refresh Data'}
              </button>
            </div>

            {error && (
              <div className="error-message">
                <strong>Error:</strong> {error}
              </div>
            )}
          </div>

          {monthData.length > 0 && (
            <div className="month-data-results">
              <h2>Month Status</h2>
              
              <div className="table-container">
                <table className="month-data-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthData.map((month, index) => (
                      <tr key={index} className={getStatusClass(month.hasData)}>
                        <td className="month-cell">{formatMonth(month.month)}</td>
                        <td className="status-cell">
                          <span className="status-icon">{getStatusIcon(month.hasData)}</span>
                          <span className="status-text">{getStatusText(month.hasData)}</span>
                        </td>
                        <td className="actions-cell">
                          {isReadyForDownload(month.month) && !month.hasData && (
                            <button
                              className="btn btn-primary btn-sm download-btn"
                              onClick={() => downloadMonth(month.month)}
                              disabled={downloadingMonth === month.month}
                            >
                              {downloadingMonth === month.month ? '⏳' : '⬇️'} Download
                            </button>
                          )}
                          {month.hasData && (
                            <button
                              className="btn btn-warning btn-sm refresh-btn"
                              onClick={() => refreshMonth(month.month)}
                              disabled={refreshingMonth === month.month}
                            >
                              {refreshingMonth === month.month ? '⏳' : '🔄'} Refresh
                            </button>
                          )}
                          {!isReadyForDownload(month.month) && !month.hasData && (
                            <span className="not-ready-text">Not ready for download</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {monthData.length === 0 && !loading && (
            <div className="no-data-message">
              <p>No QBO history data found. Click "Refresh Data" to load month information.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QBOHistory; 